import React, { useState, useEffect } from 'react';
import Layout from '../components/common/Layout';
import {
    ClipboardList,
    Plus,
    Search,
    Eye,
    Edit,
    Trash2,
    MoreHorizontal,
    Clock,
    User,
    Calendar,
    AlertCircle,
    CheckCircle,
    Wrench,
    Package,
    MapPin,
    Save,
    X,
    ArrowLeft
} from 'lucide-react';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import Loading from '../components/common/Loading';
import { ordenesService } from '../services/ordenes';
import { activosService } from '../services/activos';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

const WorkOrdersPage = () => {
    const [kanbanData, setKanbanData] = useState({
        pendientes: [],
        en_proceso: [],
        finalizadas: [],
        stats: {}
    });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedOrden, setSelectedOrden] = useState(null);
    const [activos, setActivos] = useState([]);

    // Cargar datos iniciales
    useEffect(() => {
        loadKanbanData();
        loadActivos();
    }, []);

    const loadKanbanData = async () => {
        try {
            setLoading(true);
            const data = await ordenesService.getKanban();
            setKanbanData(data);
        } catch (error) {
            toast.error('Error al cargar órdenes de trabajo');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadActivos = async () => {
        try {
            const data = await activosService.getAll({ limit: 1000 });
            // El backend devuelve { items: [...], total: n }
            setActivos(data.items || []);
        } catch (error) {
            console.error('Error loading activos:', error);
        }
    };

    const handleSearch = async () => {
        if (searchTerm.trim()) {
            try {
                const results = await ordenesService.getAll({ search: searchTerm });
                const grouped = {
                    pendientes: results.filter(o => o.estado === 'pendiente'),
                    en_proceso: results.filter(o => o.estado === 'en_proceso'),
                    finalizadas: results.filter(o => o.estado === 'finalizada'),
                    stats: kanbanData.stats
                };
                setKanbanData(grouped);
            } catch (error) {
                toast.error('Error en la búsqueda');
            }
        } else {
            loadKanbanData();
        }
    };

    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            handleSearch();
        }, 500);
        return () => clearTimeout(delayedSearch);
    }, [searchTerm]);

    const handleCreateOrden = async (ordenData) => {
        try {
            await ordenesService.create(ordenData);
            toast.success('Orden de trabajo creada correctamente');
            setShowCreateModal(false);
            loadKanbanData();
        } catch (error) {
            toast.error('Error al crear orden de trabajo');
        }
    };

    const handleChangeStatus = async (ordenId, newStatus) => {
        try {
            await ordenesService.cambiarEstado(ordenId, newStatus);
            toast.success('Estado actualizado correctamente');
            loadKanbanData();
        } catch (error) {
            toast.error('Error al cambiar estado');
        }
    };

    const handleDeleteOrden = async (ordenId) => {
        if (window.confirm('¿Estás seguro de eliminar esta orden?')) {
            try {
                await ordenesService.delete(ordenId);
                toast.success('Orden eliminada correctamente');
                loadKanbanData();
            } catch (error) {
                toast.error('Error al eliminar orden');
            }
        }
    };

    const openDetailModal = (orden, mode = 'view') => {
        setSelectedOrden({ ...orden, mode });
        setShowDetailModal(true);
    };

    if (loading) {
        return (
            <Layout>
                <Loading message="Cargando órdenes de trabajo..." />
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                    <div className="flex items-center space-x-3 mb-4 md:mb-0">
                        <ClipboardList className="h-8 w-8 text-blue-600" />
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Órdenes de Trabajo</h1>
                            <p className="text-gray-600">Gestiona las órdenes de trabajo con tablero Kanban</p>
                        </div>
                    </div>

                    <Button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center space-x-2"
                    >
                        <Plus className="h-4 w-4" />
                        <span>Nueva Orden</span>
                    </Button>
                </div>

                {/* Search and Stats */}
                <div className="mb-6">
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Buscar órdenes por número, título o activo..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                        <StatsCard
                            title="Total Órdenes"
                            value={kanbanData.stats.total || 0}
                            icon={ClipboardList}
                            color="blue"
                        />
                        <StatsCard
                            title="Pendientes"
                            value={kanbanData.stats.total_pendientes || 0}
                            icon={Clock}
                            color="yellow"
                        />
                        <StatsCard
                            title="En Proceso"
                            value={kanbanData.stats.total_en_proceso || 0}
                            icon={Wrench}
                            color="blue"
                        />
                        <StatsCard
                            title="Finalizadas"
                            value={kanbanData.stats.total_finalizadas || 0}
                            icon={CheckCircle}
                            color="green"
                        />
                    </div>
                </div>

                {/* Kanban Board */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <KanbanColumn
                        title="Tareas Pendientes"
                        orders={kanbanData.pendientes}
                        onOrderClick={openDetailModal}
                        onChangeStatus={handleChangeStatus}
                        onDelete={handleDeleteOrden}
                        status="pendiente"
                        color="yellow"
                    />
                    <KanbanColumn
                        title="Tareas en Proceso"
                        orders={kanbanData.en_proceso}
                        onOrderClick={openDetailModal}
                        onChangeStatus={handleChangeStatus}
                        onDelete={handleDeleteOrden}
                        status="en_proceso"
                        color="blue"
                    />
                    <KanbanColumn
                        title="Tareas Finalizadas"
                        orders={kanbanData.finalizadas}
                        onOrderClick={openDetailModal}
                        onChangeStatus={handleChangeStatus}
                        onDelete={handleDeleteOrden}
                        status="finalizada"
                        color="green"
                    />
                </div>

                {/* Modal para crear orden */}
                {showCreateModal && (
                    <Modal
                        title="Nueva Orden de Trabajo"
                        onClose={() => setShowCreateModal(false)}
                        size="lg"
                    >
                        <CreateOrderForm
                            activos={activos}
                            onSubmit={handleCreateOrden}
                            onClose={() => setShowCreateModal(false)}
                        />
                    </Modal>
                )}

                {/* Modal para detalles/editar orden */}
                {showDetailModal && selectedOrden && (
                    <Modal
                        title={`${selectedOrden.numero_ot} - ${selectedOrden.titulo}`}
                        onClose={() => setShowDetailModal(false)}
                        size="lg"
                    >
                        <OrderDetailModal
                            orden={selectedOrden}
                            activos={activos}
                            onChangeStatus={handleChangeStatus}
                            onUpdate={loadKanbanData}
                            onClose={() => setShowDetailModal(false)}
                            onDelete={handleDeleteOrden}
                        />
                    </Modal>
                )}
            </div>
        </Layout>
    );
};

// Componente para las estadísticas
const StatsCard = ({ title, value, icon: Icon, color }) => {
    const colorClasses = {
        blue: 'bg-blue-500',
        green: 'bg-green-500',
        yellow: 'bg-yellow-500',
        red: 'bg-red-500'
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
                <div className={`${colorClasses[color]} p-3 rounded-lg`}>
                    <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{title}</p>
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                </div>
            </div>
        </div>
    );
};

// Componente para cada columna del Kanban
const KanbanColumn = ({ title, orders, onOrderClick, onChangeStatus, onDelete, status, color }) => {
    const getHeaderColor = () => {
        switch (color) {
            case 'yellow': return 'bg-yellow-100 border-yellow-300';
            case 'blue': return 'bg-blue-100 border-blue-300';
            case 'green': return 'bg-green-100 border-green-300';
            default: return 'bg-gray-100 border-gray-300';
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md">
            <div className={`px-4 py-3 border-b-2 ${getHeaderColor()}`}>
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">{title}</h3>
                    <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
                        {orders.length}
                    </span>
                </div>
            </div>

            <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
                {orders.map((order) => (
                    <OrderCard
                        key={order.id}
                        order={order}
                        onClick={() => onOrderClick(order)}
                        onChangeStatus={onChangeStatus}
                        onDelete={onDelete}
                        currentStatus={status}
                    />
                ))}

                {orders.length === 0 && (
                    <div className="text-center py-8">
                        <ClipboardList className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">No hay tareas</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// Componente para cada tarjeta de orden
const OrderCard = ({ order, onClick, onChangeStatus, onDelete, currentStatus }) => {
    const [showMenu, setShowMenu] = useState(false);

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'alta': return 'bg-red-100 text-red-800';
            case 'media': return 'bg-yellow-100 text-yellow-800';
            case 'baja': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'correctivo': return 'bg-red-100 text-red-800';
            case 'preventivo': return 'bg-blue-100 text-blue-800';
            case 'predictivo': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No definida';
        return new Date(dateString).toLocaleDateString('es-ES');
    };

    const getNextStatus = () => {
        switch (currentStatus) {
            case 'pendiente': return 'en_proceso';
            case 'en_proceso': return 'finalizada';
            default: return null;
        }
    };

    const getStatusAction = () => {
        switch (currentStatus) {
            case 'pendiente': return 'Iniciar';
            case 'en_proceso': return 'Finalizar';
            default: return null;
        }
    };

    return (
        <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer relative">
            <div onClick={onClick}>
                {/* Header de la tarjeta */}
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h4 className="font-semibold text-gray-900 text-sm">{order.numero_ot}</h4>
                        <p className="text-xs text-gray-600">
                            {/* Las OT del plan anual por sede no tienen activo */}
                            {order.activo ? `Activo: ${order.activo.name}` : `Sede: ${order.sede || 'N/A'}`}
                        </p>
                    </div>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowMenu(!showMenu);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <MoreHorizontal className="h-4 w-4" />
                    </button>
                </div>

                {/* Título de la tarea */}
                <p className="text-sm font-medium text-gray-900 mb-2">{order.titulo}</p>

                {/* Información del activo */}
                <div className="mb-3">
                    {order.activo && <p className="text-xs text-gray-600">Cantidad: {order.activo.code}</p>}
                    <p className="text-xs text-gray-600">Tarea: {order.descripcion || 'Sin descripción'}</p>
                </div>

                {/* Duración y fecha */}
                <div className="mb-3">
                    <p className="text-xs text-gray-600">
                        Duración estimada: {order.duracion_estimada} días
                    </p>
                    <p className="text-xs text-gray-600">
                        Próxima fecha: {formatDate(order.fecha_vencimiento)}
                    </p>
                </div>

                {/* Badges de prioridad y tipo */}
                <div className="flex space-x-2 mb-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(order.prioridad)}`}>
                        Prioridad {order.prioridad}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(order.tipo_tarea)}`}>
                        {order.tipo_tarea}
                    </span>
                </div>

                {/* Información adicional */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClick(order);
                            }}
                        >
                            📋 Más información
                        </Button>
                    </div>
                </div>

                {/* Programación */}
                {order.programada_cada && (
                    <div className="mt-2 flex items-center text-xs text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />
                        Tarea programada cada {order.programada_cada}
                    </div>
                )}

                {/* Asignado a */}
                {order.asignado_a && (
                    <div className="mt-2 flex items-center text-xs text-gray-700">
                        <User className="h-3 w-3 mr-1" />
                        {order.asignado_a}
                    </div>
                )}
            </div>

            {/* Menú desplegable */}
            {showMenu && (
                <div className="absolute right-2 top-10 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
                    <button
                        onClick={() => {
                            onClick(order, 'view');
                            setShowMenu(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                        Ver detalles
                    </button>
                    <button
                        onClick={() => {
                            onClick(order, 'edit');
                            setShowMenu(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                        Editar
                    </button>
                    {getNextStatus() && (
                        <button
                            onClick={() => {
                                onChangeStatus(order.id, getNextStatus());
                                setShowMenu(false);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                        >
                            {getStatusAction()}
                        </button>
                    )}
                    <button
                        onClick={() => {
                            onDelete(order.id);
                            setShowMenu(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                    >
                        Eliminar
                    </button>
                </div>
            )}
        </div>
    );
};

// Componente CreateOrderForm
const CreateOrderForm = ({ activos, onSubmit, onClose }) => {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        numero_ot: '',
        activo_id: '',
        titulo: '',
        descripcion: '',
        estado: 'pendiente',
        prioridad: 'media',
        tipo_tarea: 'correctivo',
        fecha_inicio: '',
        fecha_fin: '',
        fecha_vencimiento: '',
        duracion_estimada: 1,
        asignado_a: '',
        creado_por: user?.full_name || '',
        costo_unitario: 0,
        costo_total: 0,
        codigos: '',
        clasificacion: '',
        notas: '',
        programada_cada: ''
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseFloat(value) || 0 : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const submitData = {
                ...formData,
                activo_id: parseInt(formData.activo_id),
                duracion_estimada: parseInt(formData.duracion_estimada),
                costo_unitario: parseFloat(formData.costo_unitario),
                costo_total: parseFloat(formData.costo_total)
            };

            if (!submitData.fecha_inicio) delete submitData.fecha_inicio;
            if (!submitData.fecha_fin) delete submitData.fecha_fin;
            if (!submitData.fecha_vencimiento) delete submitData.fecha_vencimiento;

            await onSubmit(submitData);
        } catch (error) {
            console.error('Error creating order:', error);
        } finally {
            setLoading(false);
        }
    };

    const selectedActivo = activos.find(a => a.id === parseInt(formData.activo_id));

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información básica */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Información Básica
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Activo <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="activo_id"
                            value={formData.activo_id}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Seleccionar activo</option>
                            {activos.map(activo => (
                                <option key={activo.id} value={activo.id}>
                                    {activo.code} - {activo.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Título <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="titulo"
                            value={formData.titulo}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Título de la orden de trabajo"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descripción
                    </label>
                    <textarea
                        name="descripcion"
                        value={formData.descripcion}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Descripción detallada del trabajo a realizar"
                    />
                </div>
            </div>

            {/* Información del activo seleccionado */}
            {selectedActivo && (
                <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Información del Activo</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="font-medium">Código:</span> {selectedActivo.code}
                        </div>
                        <div>
                            <span className="font-medium">Nombre:</span> {selectedActivo.name}
                        </div>
                        <div>
                            <span className="font-medium">Fabricante:</span> {selectedActivo.factory}
                        </div>
                        <div>
                            <span className="font-medium">Modelo:</span> {selectedActivo.model}
                        </div>
                        <div>
                            <span className="font-medium">Ubicación:</span> {selectedActivo.get_ubicacion_nombre ? selectedActivo.get_ubicacion_nombre() : (selectedActivo.ubicacion_obj?.nombre || 'Sin asignar')}
                        </div>
                        <div>
                            <span className="font-medium">Estado:</span>
                            <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                                selectedActivo.outservice
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-green-100 text-green-800'
                            }`}>
                                {selectedActivo.outservice ? 'Fuera de Servicio' : 'En Servicio'}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Configuración de la tarea */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Configuración de la Tarea
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Prioridad
                        </label>
                        <select
                            name="prioridad"
                            value={formData.prioridad}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="baja">Baja</option>
                            <option value="media">Media</option>
                            <option value="alta">Alta</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tipo de Tarea
                        </label>
                        <select
                            name="tipo_tarea"
                            value={formData.tipo_tarea}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="correctivo">Correctivo</option>
                            <option value="preventivo">Preventivo</option>
                            <option value="predictivo">Predictivo</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Duración Estimada (días)
                        </label>
                        <input
                            type="number"
                            name="duracion_estimada"
                            value={formData.duracion_estimada}
                            onChange={handleChange}
                            min="1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Asignado a
                        </label>
                        <input
                            type="text"
                            name="asignado_a"
                            value={formData.asignado_a}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Nombre del responsable"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Clasificación
                        </label>
                        <input
                            type="text"
                            name="clasificacion"
                            value={formData.clasificacion}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Ej: Mecánico, Eléctrico"
                        />
                    </div>
                </div>
            </div>

            {/* Fechas */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Programación
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Fecha de Inicio
                        </label>
                        <input
                            type="datetime-local"
                            name="fecha_inicio"
                            value={formData.fecha_inicio}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Fecha de Vencimiento
                        </label>
                        <input
                            type="datetime-local"
                            name="fecha_vencimiento"
                            value={formData.fecha_vencimiento}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Programada cada
                        </label>
                        <input
                            type="text"
                            name="programada_cada"
                            value={formData.programada_cada}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Ej: 6 meses, 1 año"
                        />
                    </div>
                </div>
            </div>

            {/* Costos */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Información de Costos
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Costo Unitario (S/.)
                        </label>
                        <input
                            type="number"
                            name="costo_unitario"
                            value={formData.costo_unitario}
                            onChange={handleChange}
                            step="0.01"
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Costo Total (S/.)
                        </label>
                        <input
                            type="number"
                            name="costo_total"
                            value={formData.costo_total}
                            onChange={handleChange}
                            step="0.01"
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </div>

            {/* Información adicional */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Información Adicional
                </h3>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Códigos (materiales/herramientas)
                    </label>
                    <textarea
                        name="codigos"
                        value={formData.codigos}
                        onChange={handleChange}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Códigos de materiales o herramientas necesarias"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notas
                    </label>
                    <textarea
                        name="notas"
                        value={formData.notas}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Notas adicionales sobre la orden de trabajo"
                    />
                </div>
            </div>

            {/* Botones */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                    Cancelar
                </Button>
                <Button type="submit" loading={loading}>
                    Crear Orden de Trabajo
                </Button>
            </div>
        </form>
    );
};

// Componente OrderDetailModal
const OrderDetailModal = ({ orden, activos, onChangeStatus, onUpdate, onClose, onDelete }) => {
    const [mode, setMode] = useState(orden.mode || 'view');
    const [editData, setEditData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (mode === 'edit') {
            setEditData({
                titulo: orden.titulo || '',
                descripcion: orden.descripcion || '',
                prioridad: orden.prioridad || 'media',
                tipo_tarea: orden.tipo_tarea || 'correctivo',
                duracion_estimada: orden.duracion_estimada || 1,
                asignado_a: orden.asignado_a || '',
                fecha_vencimiento: orden.fecha_vencimiento ? orden.fecha_vencimiento.split('T')[0] : '',
                costo_unitario: orden.costo_unitario || 0,
                costo_total: orden.costo_total || 0,
                codigos: orden.codigos || '',
                clasificacion: orden.clasificacion || '',
                notas: orden.notas || '',
                programada_cada: orden.programada_cada || ''
            });
        }
    }, [mode, orden]);

    const formatDate = (dateString) => {
        if (!dateString) return 'No definida';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN'
        }).format(amount || 0);
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'alta': return 'bg-red-100 text-red-800';
            case 'media': return 'bg-yellow-100 text-yellow-800';
            case 'baja': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'correctivo': return 'bg-red-100 text-red-800';
            case 'preventivo': return 'bg-blue-100 text-blue-800';
            case 'predictivo': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pendiente': return 'bg-yellow-100 text-yellow-800';
            case 'en_proceso': return 'bg-blue-100 text-blue-800';
            case 'finalizada': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'pendiente': return 'Pendiente';
            case 'en_proceso': return 'En Proceso';
            case 'finalizada': return 'Finalizada';
            default: return 'Desconocido';
        }
    };

    const handleEdit = () => setMode('edit');
    const handleCancel = () => {
        setMode('view');
        setEditData(null);
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            // Limpiar datos antes de enviar
            const submitData = {
                titulo: editData.titulo || null,
                descripcion: editData.descripcion || null,
                prioridad: editData.prioridad || null,
                tipo_tarea: editData.tipo_tarea || null,
                duracion_estimada: editData.duracion_estimada || null,
                asignado_a: editData.asignado_a || null,
                fecha_vencimiento: editData.fecha_vencimiento ? new Date(editData.fecha_vencimiento).toISOString() : null,
                costo_unitario: editData.costo_unitario || null,
                costo_total: editData.costo_total || null,
                codigos: editData.codigos || null,
                clasificacion: editData.clasificacion || null,
                notas: editData.notas || null,
                programada_cada: editData.programada_cada || null
            };
            // Eliminar campos null
            Object.keys(submitData).forEach(key => {
                if (submitData[key] === null) delete submitData[key];
            });
            await ordenesService.update(orden.id, submitData);
            toast.success('Orden actualizada correctamente');
            onUpdate();
            setMode('view');
        } catch (error) {
            toast.error('Error al actualizar la orden');
        } finally {
            setLoading(false);
        }
    };

    const handleProcessStart = () => setMode('process');
    const handleFinishStart = () => setMode('finished');

    const handleProcessConfirm = async () => {
        try {
            setLoading(true);
            await onChangeStatus(orden.id, 'en_proceso');
            toast.success('Tarea iniciada correctamente');
            onClose();
        } catch (error) {
            toast.error('Error al iniciar la tarea');
        } finally {
            setLoading(false);
        }
    };

    const handleFinishConfirm = async () => {
        try {
            setLoading(true);
            await onChangeStatus(orden.id, 'finalizada');
            toast.success('Tarea finalizada correctamente');
            onClose();
        } catch (error) {
            toast.error('Error al finalizar la tarea');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setEditData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseFloat(value) || 0 : value
        }));
    };

    // Vista principal
    if (mode === 'view') {
        return (
            <div className="space-y-6">
                {/* Header con estado */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(orden.estado)}`}>
                            {getStatusText(orden.estado)}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(orden.prioridad)}`}>
                            Prioridad {orden.prioridad}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTypeColor(orden.tipo_tarea)}`}>
                            {orden.tipo_tarea}
                        </span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button
                            onClick={handleEdit}
                            variant="outline"
                            size="sm"
                            className="flex items-center space-x-2"
                        >
                            <Edit className="h-4 w-4" />
                            <span>Editar</span>
                        </Button>
                        <Button
                            onClick={() => {
                                if (window.confirm('¿Estás seguro de eliminar esta orden?')) {
                                    onDelete(orden.id);
                                    onClose();
                                }
                            }}
                            variant="outline"
                            size="sm"
                            className="flex items-center space-x-2 text-red-600 border-red-300 hover:bg-red-50"
                        >
                            <Trash2 className="h-4 w-4" />
                            <span>Eliminar</span>
                        </Button>
                    </div>
                </div>

                {/* Información del activo (o de la sede, en las OT del plan anual) */}
                {!orden.activo && orden.sede ? (
                <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                        <MapPin className="h-5 w-5 mr-2" />
                        Sede
                    </h3>
                    <p className="text-sm text-gray-900">{orden.sede}</p>
                    <p className="text-xs text-gray-500 mt-1">Generada por el plan anual de mantenimiento (no corresponde a un solo activo).</p>
                </div>
                ) : (
                <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                        <Package className="h-5 w-5 mr-2" />
                        Información del Activo
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="font-medium text-gray-700">Código:</span>
                            <span className="ml-2 text-gray-900">{orden.activo?.code || 'N/A'}</span>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Nombre:</span>
                            <span className="ml-2 text-gray-900">{orden.activo?.name || 'N/A'}</span>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Fabricante:</span>
                            <span className="ml-2 text-gray-900">{orden.activo?.factory || 'N/A'}</span>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Modelo:</span>
                            <span className="ml-2 text-gray-900">{orden.activo?.model || 'N/A'}</span>
                        </div>
                        <div className="md:col-span-2">
                            <span className="font-medium text-gray-700">Ubicación:</span>
                            <span className="ml-2 text-gray-900 flex items-center">
                                <MapPin className="h-4 w-4 mr-1" />
                                {orden.activo?.ubicacion_nombre || 'Sin ubicación'}
                            </span>
                        </div>
                    </div>
                </div>
                )}

                {/* Detalles de la tarea */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <h3 className="font-semibold text-gray-900 border-b pb-2">Detalles de la Tarea</h3>

                        <div>
                            <label className="text-sm font-medium text-gray-600">Título</label>
                            <p className="text-gray-900">{orden.titulo}</p>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-600">Descripción</label>
                            <p className="text-gray-900">{orden.descripcion || 'Sin descripción'}</p>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-600">Duración Estimada</label>
                            <p className="text-gray-900 flex items-center">
                                <Clock className="h-4 w-4 mr-1" />
                                {orden.duracion_estimada} días
                            </p>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-600">Asignado a</label>
                            <p className="text-gray-900 flex items-center">
                                <User className="h-4 w-4 mr-1" />
                                {orden.asignado_a || 'Sin asignar'}
                            </p>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-600">Clasificación</label>
                            <p className="text-gray-900">{orden.clasificacion || 'Sin clasificación'}</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h3 className="font-semibold text-gray-900 border-b pb-2">Programación y Costos</h3>

                        <div>
                            <label className="text-sm font-medium text-gray-600">Fecha de Creación</label>
                            <p className="text-gray-900 flex items-center">
                                <Calendar className="h-4 w-4 mr-1" />
                                {formatDate(orden.fecha_creacion)}
                            </p>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-600">Fecha de Vencimiento</label>
                            <p className="text-gray-900 flex items-center">
                                <Calendar className="h-4 w-4 mr-1" />
                                {formatDate(orden.fecha_vencimiento)}
                            </p>
                        </div>

                        {orden.fecha_inicio && (
                            <div>
                                <label className="text-sm font-medium text-gray-600">Fecha de Inicio</label>
                                <p className="text-gray-900 flex items-center">
                                    <Calendar className="h-4 w-4 mr-1" />
                                    {formatDate(orden.fecha_inicio)}
                                </p>
                            </div>
                        )}

                        <div>
                            <label className="text-sm font-medium text-gray-600">Costo Unitario</label>
                            <p className="text-gray-900">{formatCurrency(orden.costo_unitario)}</p>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-600">Costo Total</label>
                            <p className="text-gray-900">{formatCurrency(orden.costo_total)}</p>
                        </div>
                    </div>
                </div>

                {/* Códigos y notas */}
                {(orden.codigos || orden.notas) && (
                    <div className="space-y-4">
                        <h3 className="font-semibold text-gray-900 border-b pb-2">Información Adicional</h3>

                        {orden.codigos && (
                            <div>
                                <label className="text-sm font-medium text-gray-600">Códigos</label>
                                <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{orden.codigos}</p>
                            </div>
                        )}

                        {orden.notas && (
                            <div>
                                <label className="text-sm font-medium text-gray-600">Notas</label>
                                <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{orden.notas}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Acciones según el estado */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                    {orden.estado === 'pendiente' && (
                        <Button
                            onClick={handleProcessStart}
                            className="flex items-center space-x-2"
                        >
                            <Wrench className="h-4 w-4" />
                            <span>Iniciar Tarea</span>
                        </Button>
                    )}

                    {orden.estado === 'en_proceso' && (
                        <Button
                            onClick={handleFinishStart}
                            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700"
                        >
                            <CheckCircle className="h-4 w-4" />
                            <span>Finalizar Tarea</span>
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    // Modo de edición
    if (mode === 'edit' && editData) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Editar Orden de Trabajo</h3>
                    <Button
                        onClick={handleCancel}
                        variant="outline"
                        size="sm"
                        className="flex items-center space-x-2"
                    >
                        <X className="h-4 w-4" />
                        <span>Cancelar</span>
                    </Button>
                </div>

                <form className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
                            <input
                                type="text"
                                name="titulo"
                                value={editData.titulo || ''}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Asignado a</label>
                            <input
                                type="text"
                                name="asignado_a"
                                value={editData.asignado_a || ''}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                        <textarea
                            name="descripcion"
                            value={editData.descripcion || ''}
                            onChange={handleInputChange}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
                            <select
                                name="prioridad"
                                value={editData.prioridad || 'media'}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="baja">Baja</option>
                                <option value="media">Media</option>
                                <option value="alta">Alta</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Tarea</label>
                            <select
                                name="tipo_tarea"
                                value={editData.tipo_tarea || 'correctivo'}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="correctivo">Correctivo</option>
                                <option value="preventivo">Preventivo</option>
                                <option value="predictivo">Predictivo</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Duración (días)</label>
                            <input
                                type="number"
                                name="duracion_estimada"
                                value={editData.duracion_estimada || 1}
                                onChange={handleInputChange}
                                min="1"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </form>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                    <Button onClick={handleCancel} variant="outline">
                        Cancelar
                    </Button>
                    <Button onClick={handleSave} loading={loading}>
                        <Save className="h-4 w-4 mr-2" />
                        Guardar Cambios
                    </Button>
                </div>
            </div>
        );
    }

    // Modo confirmación de inicio de proceso
    if (mode === 'process') {
        return (
            <div className="space-y-6">
                <div className="flex items-center space-x-3">
                    <Button onClick={() => setMode('view')} variant="outline" size="sm">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <h3 className="text-lg font-semibold text-blue-900">En proceso</h3>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-4">{orden.titulo}</h4>

                    <div className="space-y-3 text-sm">
                        <div>
                            <span className="font-medium text-gray-700">Creado por:</span>
                            <span className="ml-2">{orden.creado_por}</span>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Fecha de creación:</span>
                            <span className="ml-2">{formatDate(orden.fecha_creacion)}</span>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Notas:</span>
                            <p className="mt-1 text-gray-900">{orden.notas || 'Sin notas adicionales'}</p>
                        </div>
                    </div>

                    <div className="mt-6">
                        <Button
                            onClick={handleProcessConfirm}
                            loading={loading}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            Confirmar Inicio de Proceso
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Modo confirmación de finalización
    if (mode === 'finished') {
        return (
            <div className="space-y-6">
                <div className="flex items-center space-x-3">
                    <Button onClick={() => setMode('view')} variant="outline" size="sm">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <h3 className="text-lg font-semibold text-green-900">En proceso</h3>
                </div>

                <div className="bg-green-50 p-6 rounded-lg">
                    <h4 className="font-semibold text-green-900 mb-4">{orden.titulo}</h4>

                    <div className="space-y-3 text-sm">
                        <div>
                            <span className="font-medium text-gray-700">Creado por:</span>
                            <span className="ml-2">{orden.creado_por}</span>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Fecha de creación:</span>
                            <span className="ml-2">{formatDate(orden.fecha_creacion)}</span>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Notas:</span>
                            <p className="mt-1 text-gray-900">{orden.notas || 'Sin notas adicionales'}</p>
                        </div>
                    </div>

                    <div className="mt-6">
                        <Button
                            onClick={handleFinishConfirm}
                            loading={loading}
                            className="bg-green-600 hover:bg-green-700 text-white"
                        >
                            Confirmar Finalización
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default WorkOrdersPage;